import { AiOutlineCloseCircle } from "react-icons/ai";
import { useSeller } from "../../../context/seller";
import Modal from "../../common/Modal";
import { FormEvent, useEffect, useMemo, useState } from "react";
import * as CommonTypes from '../../../types/common.types'
import SellerService from "../../../services/seller.service";
import { useGlobal } from "../../../context/global";
import ToursService from "../../../services/tours.service";
import Select from "../../common/Select";
import repartir from "../../../utils/repartir";
import useEntradasYPago, { EntradasYPago } from "../EntradasYPago";

// 'yyyy-MM-dd' de la fecha elegida en el calendario (Date local) o de un texto.
function fechaTexto(fecha: any): string {
    if (!fecha) return ''
    if (typeof fecha === 'string') return fecha.slice(0, 10)
    const d = fecha as Date
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default function TicketPaymentGroup() {
    const { setMessage, setIsLoading } = useGlobal()
    const { newGroup, setNewGroupReservations, setSummary, setIsConfirmBoucherModalOpen, 
        setIsTicketPaymentGroupModalOpen, newGroupReservationData, setNewGroupReservationData, user } = useSeller()
    const [tour, setTour] = useState<CommonTypes.TourInfo>()
    const [otroPrecio, setOtroPrecio] = useState(false)

    useEffect(() => {
        async function fetchTours() {
            try {
                const response = await ToursService.getDetailTourInfo(newGroupReservationData?.tour_id || '', user?.user.agency_id || '')
                setTour(response.data)
            } catch (error) {
                console.error('Error fetching tours:', error)
            }
        }
        fetchTours()
    }, [newGroupReservationData?.tour_id, user?.user.agency_id])

    const price_options = useMemo(() => {
        if (!tour) return []
        const options = []
        options.push({value: tour.current_price, name: `Current Price: $${tour.current_price}`})
        if (tour.discount_price) options.push({value: tour.discount_price, name: `Discount Price: $${tour.discount_price}`})
        if (tour.depart_price) options.push({value: tour.depart_price, name: `Depart Price: $${tour.depart_price}`})
        // Cuarta opción (16/10/2026): el vendedor escribe el precio que acordó con el pasajero.
        options.push({value: 'otro', name: 'Otro precio'})
        return options
    }, [tour])

    // Tour privado: se cobra por vehículo. Precio y pagos se cargan como total del grupo y se reparten.
    const privado = tour?.unidad_precio === 'por_vehiculo'
    const parte = privado && newGroupReservationData?.price ? repartir(newGroupReservationData.price, newGroup.length)[0] : 0
    const sobreCupo = privado && !!tour?.capacidad_ofrecida && newGroup.length > tour.capacidad_ofrecida

    // Entradas de cada pasajero (calculadas por el backend) y pago por porcentaje (16/10/2026).
    const precio = Number(newGroupReservationData?.price) || 0
    const entradasYPago = useEntradasYPago({
        tourId: newGroupReservationData?.tour_id,
        fecha: newGroupReservationData?.reservation_date,
        pasajeros: newGroup.map(p => ({ id: p.id as string, name: p.name })),
        preciosTour: privado ? repartir(precio, newGroup.length) : newGroup.map(() => precio),
    })

    async function onSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault()
        console.log(newGroup);
        if (!newGroupReservationData?.price) {
            setMessage({ type: 'error', content: 'Elige el precio del tour.' })
            setTimeout(() => setMessage(undefined), 5000)
            return
        }
        if (entradasYPago.falta) {
            setMessage({ type: 'error', content: entradasYPago.falta })
            setTimeout(() => setMessage(undefined), 6000)
            return
        }
        if (!newGroup.length) return
        // Botón "Agregar tour" (17/10/2026): si el grupo ya tiene este privado ese día, sumarse ahí
        // repartiría el vehículo con los que ya estaban (y lo que ya pagaron). Como el "+" de antes, no se ofrece.
        const yaTiene = (newGroupReservationData as any).tours_existentes?.some((t: any) =>
            t.tour_id === newGroupReservationData.tour_id && t.date === fechaTexto(newGroupReservationData.reservation_date))
        if (privado && yaTiene) {
            setMessage({ type: 'error', content: 'Ese grupo ya tiene este tour privado ese día. Para sumar a alguien al mismo vehículo, edita el privado; si es otro vehículo, hazlo como reserva nueva aparte.' })
            setTimeout(() => setMessage(undefined), 8000)
            return
        }
        setIsLoading(true)
        // En un privado se manda el total del vehículo en cada reserva y el backend lo reparte entre
        // todas: la parte final de cada pasajero es esta (la respuesta de los primeros queda vieja).
        const partes = privado ? repartir(newGroupReservationData.price, newGroup.length) : []
        // Sin monto de entradas (lo calcula el backend) y sin pago: el pago de toda la venta se registra al
        // final (PagoVenta, 17/10/2026). tours_existentes es solo para el aviso de arriba.
        const { tickets, tours_existentes, join_reservation_id, ...datos } = newGroupReservationData as any
        // "Agregar tour" a una reserva suelta: la primera reserva la junta (crea el grupo) y las demás van a ese grupo.
        let group_id: string | undefined = datos.group_id
        try {
            for (let i = 0; i < newGroup.length; i++) {
                const passenger = newGroup[i];
                const response = (await SellerService.createReservation({
                    ...datos,
                    ...(group_id ? { group_id } : join_reservation_id ? { join_reservation_id } : {}),
                    passenger_id: passenger.id,
                    ticket_included: entradasYPago.ticketIncluded,
                    payment_methods: [],
                } as CommonTypes.SubmitReservation))
                if (!group_id && response.data?.reservation?.group_id) {
                    group_id = response.data.reservation.group_id as string
                    const nuevo = group_id
                    setNewGroupReservationData(prev => ({ ...prev, group_id: nuevo, join_reservation_id: undefined } as any))
                }
                const price = privado ? partes[i] : newGroupReservationData.price
                setSummary(prev => ([...prev, {...response.data, reservation: {...response.data.reservation, price}}]))
                setMessage({
                    type: 'success',
                    content: 'Passengers successfully assigned to tour'
                })
                setNewGroupReservations(prev => [...prev, {
                    id: response.data.reservation.id,
                    price,
                    tickets: Number(response.data.reservation.tickets || 0),
                    tour_id: newGroupReservationData.tour_id,
                    reservation_date: newGroupReservationData.reservation_date,
                    seller_remark: newGroupReservationData.seller_remark || '',
                    // El pago se registra al final, para toda la venta.
                    payment_methods: [],
                } as unknown as CommonTypes.Reservation])
            }
            setIsConfirmBoucherModalOpen(true)
        } catch (error: any) {
            setMessage({
                type: 'error',
                content: typeof error?.response?.data === 'string' ? error.response.data : 'An error occurred, please try again'
            })
        }
        finally {
            setIsLoading(false)
            setTimeout(() => setMessage(undefined), 5000)
        }
        console.log(newGroupReservationData)
        
    }
    console.log(tour)
    return (
        <Modal onSubmit={onSubmit}>
            <h1>Precio y entradas</h1>
            {
                privado
                ? <h3>Tour privado: precio y pagos del vehículo completo, se dividen entre los {newGroup.length} pasajeros*</h3>
                : <h3>Prices per passenger, not general*</h3>
            }
            <Select
            style={{margin: '1rem 0'}}
            defaultNull={true}
            label={privado ? "Precio del privado (vehículo completo)" : "Sale Price Options"}
            options={price_options}
            onChange={(e) => {
                const otro = e.target.value === 'otro'
                setOtroPrecio(otro)
                setNewGroupReservationData({...newGroupReservationData, price: otro ? undefined : Number(e.target.value)})
            }}
            />
            {
                otroPrecio &&
                <label className="otro-precio" style={{display: "flex", flexDirection: "column", gap: 4, margin: "-6px 0 12px", fontSize: 14}}>
                    {privado ? 'Precio del privado (vehículo completo)' : 'Precio por pasajero'}
                    <input type="number" min={1} placeholder="Ej: 35000" aria-label="Otro precio"
                    value={newGroupReservationData?.price || ''}
                    onChange={e => setNewGroupReservationData({...newGroupReservationData, price: Number(e.target.value) || undefined})} />
                </label>
            }
            {
                privado && parte > 0 &&
                <p style={{margin: '-6px 0 12px', fontSize: '14px'}}>{newGroup.length} pasajeros × ~${parte} cada uno</p>
            }
            {
                sobreCupo &&
                <p style={{margin: '-6px 0 12px', fontSize: '14px', color: '#b45309'}}>Ojo: el privado es para {tour?.capacidad_ofrecida} pasajeros y el grupo tiene {newGroup.length}. Si van en más de un vehículo, el precio tiene que ser el de todos.</p>
            }
            <EntradasYPago ctrl={entradasYPago} sinPago />
            <p style={{fontSize: 13, color: '#555', marginTop: 8}}>El pago se registra al final, para toda la venta.</p>
            <div className="actions">
                <button type="submit">Continue</button>
            </div>
            <div className="close" 
            onClick={() => setIsTicketPaymentGroupModalOpen(false)}
            title="close">
                <AiOutlineCloseCircle/>
            </div>
        </Modal>
    )
}
